import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { compact, isEmpty, isNil } from 'lodash';

import { useChatController } from '~/components/AiChat/controller/ChatControllerContext';
import { useChatMixpanel } from '~/components/AiChat/hooks/useChatMixpanel';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Tooltip from '~/components/core/Atomic/Tooltip';
import { NewRefreshIcon } from '~/components/icons';
import SendMessageIcon from '~/components/icons/SendMessageIcon';
import { CLIVE_CHAT_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import cn from '~/Utils/cn';

import { CHAT_V2_LAYOUT } from '../../constants/chatStyling';

import { ChatInputAttachMenu } from './ChatInputAttachMenu';

type ChatInputV2Density = 'default' | 'compact';

interface DensityStyles {
  box: string;
  gap: string;
  text: string;
  multilineMaxHeight: string;
  sendButton: string;
  sendIconSize: number;
}

/** What the density sizes: the box around the field, the field's text and the Send button. */
const DENSITY_STYLES: Record<ChatInputV2Density, DensityStyles> = {
  default: {
    box: 'm-16 border-2 p-16 shadow-md',
    gap: 'gap-12',
    text: 'text-lg',
    multilineMaxHeight: 'max-h-[200px]',
    sendButton: 'h-40 w-40',
    sendIconSize: 20,
  },
  compact: {
    box: 'my-8 border px-12 py-8 shadow-sm',
    gap: 'gap-8',
    text: 'text-base',
    multilineMaxHeight: 'max-h-[120px]',
    sendButton: 'h-32 w-32',
    sendIconSize: 16,
  },
};

export interface ChatInputV2Props {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => Promise<void>;
  onPromptSubmit?: (promptTitle: string, promptKey: string) => Promise<void>;
  disabled?: boolean;
  /** Defaults to the mounted chat's placeholder. */
  placeholder?: string;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
  onMultilineChange?: (isMultiline: boolean) => void;
  /** The + menu of documents and prompts; off where a message has no chat to add them to. Shown only in a claim's chat. */
  showAttachMenu?: boolean;
  /** The Clive chat's size by default; compact where the field sits under content it should not crowd. */
  density?: ChatInputV2Density;
  /** Keeps the field, and its focus, while taking no text and sending nothing: Enter does not send, nor does Send. */
  readOnly?: boolean;
  /** The field's last send failed: a red box, Send turned into a red Retry named `invalidLabel`, and the field marked invalid. */
  invalid?: boolean;
  /** The Retry button's name and tooltip while invalid. */
  invalidLabel?: string;
  /** What else a screen reader is told of the failure, with the label, as the field's description. Not drawn. */
  invalidDescription?: string | null;
}

export const ChatInputV2: React.FC<ChatInputV2Props> = ({
  value,
  onChange,
  onSubmit,
  onPromptSubmit,
  disabled = false,
  placeholder,
  inputRef,
  onMultilineChange,
  showAttachMenu = true,
  density = 'default',
  readOnly = false,
  invalid = false,
  invalidLabel,
  invalidDescription,
}) => {
  const styles = DENSITY_STYLES[density];
  const invalidLabelId = useId();
  const invalidDescriptionId = useId();
  // What the box says beside Send, and the reason a screen reader is told with it: nothing while valid.
  const shownInvalidLabel = invalid && !isEmpty(invalidLabel) ? invalidLabel : null;
  const shownInvalidDescription = invalid && !isEmpty(invalidDescription) ? invalidDescription : null;
  const describedBy = compact([
    !isNil(shownInvalidLabel) && invalidLabelId,
    !isNil(shownInvalidDescription) && invalidDescriptionId,
  ]).join(' ');
  const controller = useChatController();
  // The menu's documents, uploads and prompts belong to a claim, so a chat without one gets no menu.
  const attachMenuFeatures = showAttachMenu ? controller?.claim : undefined;
  const { trackChatEvent } = useChatMixpanel();

  const [isMultiline, setIsMultiline] = useState(false);
  const cursorPositionRef = useRef<{ start: number; end: number } | null>(null);

  const internalRef = useRef<HTMLTextAreaElement>(null);
  const resolvedRef = inputRef ?? internalRef;

  useEffect(() => {
    const textarea = resolvedRef.current;
    if (!textarea) return;

    // Save cursor position before potential layout change
    cursorPositionRef.current = {
      start: textarea.selectionStart,
      end: textarea.selectionEnd,
    };

    if (!value.trim()) {
      setIsMultiline(false);
      return;
    }

    if (textarea.scrollHeight > textarea.clientHeight) {
      setIsMultiline(true);
    }
  }, [value, resolvedRef]);

  useEffect(() => {
    onMultilineChange?.(isMultiline);
  }, [isMultiline, onMultilineChange]);

  useEffect(() => {
    const textarea = resolvedRef.current;
    if (!textarea) return;

    textarea.focus();

    // Restore saved cursor position, or default to end if no position saved
    if (cursorPositionRef.current) {
      textarea.setSelectionRange(cursorPositionRef.current.start, cursorPositionRef.current.end);
    } else {
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    }
  }, [isMultiline, resolvedRef]);

  useLayoutEffect(() => {
    const textarea = inputRef?.current;
    if (!textarea) return;

    if (isMultiline) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    } else {
      textarea.style.height = '';
    }
  }, [value, isMultiline, inputRef]);

  const selectedDocumentIds = controller?.claim?.selectedDocumentIds;
  const trackAndSubmit = useCallback(async () => {
    if (value.trim()) {
      trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_MESSAGE_SENT, {
        message_length: value.length,
        has_documents: !isEmpty(selectedDocumentIds),
      });
    }
    await onSubmit();
  }, [onSubmit, trackChatEvent, value, selectedDocumentIds]);

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (!readOnly) await trackAndSubmit();
      }
    },
    [readOnly, trackAndSubmit]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  const sharedTextareaProps = {
    id: 'chat-input',
    name: 'chat-input',
    ref: inputRef,
    value,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    placeholder: placeholder ?? controller?.presentation.placeholder ?? 'Type here',
    disabled,
    readOnly,
    rows: 1,
    'aria-invalid': invalid || undefined,
    'aria-describedby': isEmpty(describedBy) ? undefined : describedBy,
  };

  return (
    <div
      className={cn(
        // Ahead of `mx-auto`: `cn` drops a margin a later one covers, so `m-16` after it would undo the centring.
        styles.box,
        'mx-auto flex self-center rounded-xl border-solid',
        invalid ? 'border-red-700' : 'border-azure-300',
        'bg-white',
        isMultiline ? 'flex-col' : 'items-center',
        styles.gap,
        CHAT_V2_LAYOUT.maxWidthClass
      )}
    >
      {isMultiline && (
        <textarea
          {...sharedTextareaProps}
          className={cn(
            'resize-none overflow-y-auto border-none bg-transparent font-serif text-slate-900 outline-none transition-[height] duration-200 ease-in-out placeholder:text-slate-500',
            styles.multilineMaxHeight,
            styles.text
          )}
        />
      )}

      <div
        className={cn(
          isMultiline ? 'flex w-full items-center' : 'contents',
          isMultiline && (attachMenuFeatures ? 'justify-between' : 'justify-end')
        )}
      >
        {attachMenuFeatures && (
          <ChatInputAttachMenu claimFeatures={attachMenuFeatures} disabled={disabled} onPromptSubmit={onPromptSubmit} />
        )}

        {!isMultiline && (
          <textarea
            {...sharedTextareaProps}
            className={cn(
              'flex-1 resize-none border-none bg-transparent font-serif text-slate-900 outline-none placeholder:text-slate-500',
              styles.text
            )}
          />
        )}

        {!isNil(shownInvalidDescription) ? (
          <span id={invalidDescriptionId} hidden>
            {shownInvalidDescription}
          </span>
        ) : null}
        {/* Send turns into Retry, the same size, so a failure changes neither the box's width nor its height. The
            alert sits inside the button, positioned, so a screen reader says it as it appears and it cannot
            stretch the page: a change to the focused field's description is not announced. */}
        {!isNil(shownInvalidLabel) ? (
          <Tooltip title={shownInvalidLabel} placement="top">
            <IconButton
              className={cn(
                'hover:bg-red-600 relative flex shrink-0 items-center justify-center rounded-full bg-red-700 text-white disabled:cursor-not-allowed disabled:bg-red-700 disabled:opacity-50',
                styles.sendButton
              )}
              onClick={trackAndSubmit}
              disabled={disabled || readOnly}
              aria-label={shownInvalidLabel}
            >
              <NewRefreshIcon iconColor="white" size={styles.sendIconSize} />
              <span id={invalidLabelId} role="alert" className="sr-only">
                {shownInvalidLabel}
              </span>
            </IconButton>
          </Tooltip>
        ) : (
          <IconButton
            className={cn(
              'flex shrink-0 items-center justify-center rounded-full bg-pink-600 text-white hover:bg-pink-500 disabled:cursor-not-allowed disabled:bg-pink-600 disabled:opacity-50',
              styles.sendButton
            )}
            onClick={trackAndSubmit}
            disabled={disabled || readOnly}
            aria-label="Send message"
          >
            <SendMessageIcon iconColor="white" size={styles.sendIconSize} />
          </IconButton>
        )}
      </div>
    </div>
  );
};
